import { useEffect, useState } from 'react';
import AppWrappedSearchableAutocomplete from '../../AppWrappedSearchableAutocomplete';
import { fetchPositionData, fetchPositionDefaultData } from '../../79/_api';
import { EPIC_FIELDS, EPIC_FIELD_KEYS } from '../../79/_const';
import PositionHelperLinks from './PositionHelperLinks';

/**
 * Position input component for Epic/EpicCare stylized to work with Custom App Request Form
 * @param {Object} props properties
 * @returns Custom input
 */
const PositionField = props => {
	const POSITION_API_FAILURE_MESSAGE =
		'Unable to find any positions based on your input.  Please try again.';
	const [loadingStatus, setLoadingStatus] = useState(true);
	const [optionsList, setOptionsList] = useState([]);
	const [defaultValue, setDefaultValue] = useState(
		EPIC_FIELDS[props.name]?.defaultValue ?? ''
	);

	useEffect(() => {
		//query for default value
		fetchPositionDefaultData()
			.then(response => {
				setDefaultValue(response);
			})
			.catch(e =>
				props.formik.setStatus({
					error: {
						source: props.name,
						cause: 'API Failure',
						details: e,
						message:
							'Unable to determine the default position to populate.  Please try again.',
					},
				})
			);

		//query for positions to display in dropdown
		fetchPositionData()
			.then(response => {
				setOptionsList(response);
				setLoadingStatus(false);
			})
			.catch(e =>
				props.formik.setStatus({
					error: {
						source: props.name,
						cause: 'API Failure',
						details: e,
						message:
							'Unable to search for positions based on your input.  Please try again.',
					},
				})
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (props.isDisabled) {
			props.formik.setFieldValue(props.name, defaultValue);
		} else {
			if (
				!(EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT in props?.formik?.errors)
			) {
				props.formik.setFieldValue(props.name, '');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isDisabled]);

	/**
	 * Take user input and return a list of search results
	 * @param {string} value user input to search for
	 * @returns {string[]|object[]} list of results based on `value`
	 */
	const onSearchHandler = async value => {
		if (value) {
			let results = [...optionsList];
			if (value.length > 0) {
				results = optionsList.filter(o =>
					o.toLowerCase().includes(value.toLowerCase())
				);
			}
			return results.length > 200 ? results.slice(0, 200) : results;
		} else {
			return [];
		}
	};

	/**
	 * Process search results
	 * @param {string[]|object[]} resultsArr list of search results
	 * @returns {object[]} array of `key`, `value` pairs based on `resultsArr`
	 */
	const onSearchSuccess = resultsArr => {
		return resultsArr
			.map(key => ({
				key: key,
				value: key,
				category: 'Search Results',
			}))
			.concat(
				optionsList
					.filter(opt => !resultsArr.includes(opt))
					.map(key => ({
						key: key,
						value: key,
						category: 'Other Options',
					}))
			);
	};

	/**
	 * Gracefully handle errors during `onSearch` execution of `AppWrappedSearchableAutocomplete`
	 * @param {object} error error caught during `onSearch` workflow
	 * @returns {string[]} default list of results
	 */
	const onSearchFailure = error => {
		props.formik.setStatus({
			error: {
				source: props.name,
				cause: 'Input Failure',
				details: error,
				message: POSITION_API_FAILURE_MESSAGE,
			},
		});
		return [];
	};

	return (
		<AppWrappedSearchableAutocomplete
			label='Position'
			onSearchHandler={onSearchHandler}
			onSearchFailure={onSearchFailure}
			onSearchSuccess={onSearchSuccess}
			isLoading={loadingStatus}
			helperText={
				props.isDisabled ? (
					<>
						This field is disabled with the selection of{' '}
						<em>CLINICAL VIEW ONLY</em> because you have not
						provided a valid Default Department above.
					</>
				) : (
					<>
						Type to search for Positions.
						<br />
						<PositionHelperLinks />
					</>
				)
			}
			{...props}
		/>
	);
};

export default PositionField;
